export interface ShoppingListItem {
  id: string;
  name: string;
  quantity: string | null;
  category: string | null;
  notes: string | null;
  checked: boolean;
  source: "manual" | "pantry";
  createdAt: string;
  checkedAt: string | null;
}

export interface ShoppingListState {
  version: 1;
  items: ShoppingListItem[];
  updatedAt: string;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const STORAGE_KEY = "pantryrx.shoppingList.v1";

const FALLBACK_CATEGORY = "Uncategorized";

function emptyState(): ShoppingListState {
  return { version: 1, items: [], updatedAt: new Date(0).toISOString() };
}

function isShoppingListItem(value: unknown): value is ShoppingListItem {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.id === "string" &&
    typeof record.name === "string" &&
    typeof record.checked === "boolean" &&
    (record.source === "manual" || record.source === "pantry") &&
    typeof record.createdAt === "string"
  );
}

function parseState(raw: string | null): ShoppingListState {
  if (!raw) return emptyState();
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return emptyState();
    const record = parsed as Record<string, unknown>;
    const items = Array.isArray(record.items) ? record.items.filter(isShoppingListItem) : [];
    const updatedAt = typeof record.updatedAt === "string" ? record.updatedAt : new Date(0).toISOString();
    return { version: 1, items, updatedAt };
  } catch {
    return emptyState();
  }
}

function defaultStorage(): StorageLike | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function loadShoppingList(storage: StorageLike | null = defaultStorage()): ShoppingListState {
  if (!storage) return emptyState();
  return parseState(storage.getItem(STORAGE_KEY));
}

export function saveShoppingList(
  state: ShoppingListState,
  storage: StorageLike | null = defaultStorage(),
): void {
  if (!storage) return;
  const payload = JSON.stringify({ ...state, updatedAt: new Date().toISOString() });
  storage.setItem(STORAGE_KEY, payload);
}

export function normalizeCategoryLabel(value: string | null | undefined): string {
  const trimmed = (value || "").trim();
  if (!trimmed) return FALLBACK_CATEGORY;
  return trimmed
    .split(/\s+/)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(" ");
}

function generateId(): string {
  const cryptoApi = typeof globalThis !== "undefined" ? (globalThis as { crypto?: Crypto }).crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  return `sl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface AddShoppingListItemInput {
  name: string;
  quantity?: string | null;
  category?: string | null;
  notes?: string | null;
  source?: "manual" | "pantry";
}

export function addItem(state: ShoppingListState, input: AddShoppingListItemInput): ShoppingListState {
  const trimmedName = (input.name || "").trim();
  if (!trimmedName) return state;

  const normalizedCategory = normalizeCategoryLabel(input.category);
  const lowerName = trimmedName.toLowerCase();
  const existingIndex = state.items.findIndex(
    (item) =>
      !item.checked &&
      item.name.toLowerCase() === lowerName &&
      normalizeCategoryLabel(item.category) === normalizedCategory,
  );

  if (existingIndex >= 0) {
    const next = state.items.slice();
    const existing = next[existingIndex];
    next[existingIndex] = {
      ...existing,
      quantity: mergeQuantity(existing.quantity, input.quantity ?? null),
      notes: existing.notes || (input.notes ?? null),
    };
    return { ...state, items: next };
  }

  const item: ShoppingListItem = {
    id: generateId(),
    name: trimmedName,
    quantity: (input.quantity ?? "").trim() || null,
    category: normalizedCategory,
    notes: (input.notes ?? "").trim() || null,
    checked: false,
    source: input.source ?? "manual",
    createdAt: new Date().toISOString(),
    checkedAt: null,
  };
  return { ...state, items: [item, ...state.items] };
}

function mergeQuantity(left: string | null, right: string | null): string | null {
  const a = (left || "").trim();
  const b = (right || "").trim();
  if (!a) return b || null;
  if (!b) return a;
  const tokens = a.split("+").map((token) => token.trim().toLowerCase()).filter(Boolean);
  if (tokens.includes(b.toLowerCase())) return a;
  return `${a} + ${b}`;
}

export function toggleItem(state: ShoppingListState, id: string): ShoppingListState {
  const items = state.items.map((item) => {
    if (item.id !== id) return item;
    const checked = !item.checked;
    return {
      ...item,
      checked,
      checkedAt: checked ? new Date().toISOString() : null,
    };
  });
  return { ...state, items };
}

export function removeItem(state: ShoppingListState, id: string): ShoppingListState {
  return { ...state, items: state.items.filter((item) => item.id !== id) };
}

export function clearChecked(state: ShoppingListState): ShoppingListState {
  return { ...state, items: state.items.filter((item) => !item.checked) };
}

export function clearAll(state: ShoppingListState): ShoppingListState {
  return { ...state, items: [] };
}

export function updateItem(
  state: ShoppingListState,
  id: string,
  patch: Partial<Pick<ShoppingListItem, "name" | "quantity" | "category" | "notes">>,
): ShoppingListState {
  const items = state.items.map((item) => {
    if (item.id !== id) return item;
    const nextName = patch.name !== undefined ? patch.name.trim() : item.name;
    return {
      ...item,
      name: nextName || item.name,
      quantity: patch.quantity !== undefined ? (patch.quantity ?? "").trim() || null : item.quantity,
      category:
        patch.category !== undefined ? normalizeCategoryLabel(patch.category ?? null) : item.category,
      notes: patch.notes !== undefined ? (patch.notes ?? "").trim() || null : item.notes,
    };
  });
  return { ...state, items };
}

export interface ShoppingListSummary {
  total: number;
  remaining: number;
  completed: number;
  categories: string[];
}

export function summarize(state: ShoppingListState): ShoppingListSummary {
  const categories = new Set<string>();
  let remaining = 0;
  let completed = 0;
  for (const item of state.items) {
    categories.add(normalizeCategoryLabel(item.category));
    if (item.checked) {
      completed += 1;
    } else {
      remaining += 1;
    }
  }
  return {
    total: state.items.length,
    remaining,
    completed,
    categories: Array.from(categories).sort((a, b) => a.localeCompare(b)),
  };
}

export interface GroupedShoppingList {
  category: string;
  items: ShoppingListItem[];
}

export function groupByCategory(items: ShoppingListItem[]): GroupedShoppingList[] {
  const buckets = new Map<string, ShoppingListItem[]>();
  for (const item of items) {
    const key = normalizeCategoryLabel(item.category);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      buckets.set(key, [item]);
    }
  }
  return Array.from(buckets.entries())
    .map(([category, list]) => ({ category, items: list }))
    .sort((a, b) => a.category.localeCompare(b.category));
}

export function formatAsText(state: ShoppingListState): string {
  const active = state.items.filter((item) => !item.checked);
  if (active.length === 0) {
    return "Shopping list is empty.";
  }
  const groups = groupByCategory(active);
  const lines: string[] = ["Shopping list", ""];
  for (const group of groups) {
    lines.push(group.category);
    for (const item of group.items) {
      const qty = item.quantity ? ` (${item.quantity})` : "";
      lines.push(`- ${item.name}${qty}`);
    }
    lines.push("");
  }
  return lines.join("\n").trimEnd();
}

export const SHOPPING_LIST_STORAGE_KEY = STORAGE_KEY;
